import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, tap, map } from 'rxjs/operators';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IDeleteParams,
  IStoreProps,
  IListParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { PageEvent } from '@angular/material/paginator';
import { IFruitNinjaGame } from './fruit-ninja-game.interface';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'fruitNinja-games';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IFruitNinjaGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class FruitNinjaGameCoreService {
  private _loading$ = new BehaviorSubject<boolean>(false);

  /**
   * @name fruitNinjaGameList$
   * @description emits stored list of the Fruit Ninja Game list
   */
  fruitNinjaGameList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 400);
    } else {
      this.refresh();
    }
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public getFruitNinjaGameList(params?: IListParams): void {
    this._loading$.next(true);
    this._api
      .get(`fruit-ninja-game/list?limit=1000&skip=0`, params || {})
      .pipe(debounceTime(10000))
      .subscribe((data: IfruitNinjaGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading$.next(false);
      });
  }

  public getFruitNinjaGame(
    fruitNinjaGameId: string,
  ): Observable<IFruitNinjaGame> {
    return this._api.get(`fruit-ninja-game/${fruitNinjaGameId}`);
  }

  public addFruitNinjaGame(
    fruitNinjaGame: IFruitNinjaGame,
  ): Observable<IFruitNinjaGame> {
    console.log('fruitNinjaGame: ', fruitNinjaGame);
    return this._api
      .post(`fruit-ninja-game`, fruitNinjaGame)
      .pipe(tap((data: IFruitNinjaGame) => store.update(addEntities([data]))));
  }

  public updateFruitNinjaGame(
    fruitNinjaGame: IFruitNinjaGame,
  ): Observable<IFruitNinjaGame> {
    return this._api.put(`fruit-ninja-game`, fruitNinjaGame).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IFruitNinjaGame) =>
        store.update(
          updateEntities(data._id, (entity) => ({
            ...entity,
            name: data.name,
          })),
        ),
      ),
    );
  }

  public deleteFruitNinjaGame(data: IDeleteParams): Observable<any> {
    return this._api
      .delete('fruit-ninja-game', { body: data })
      .pipe(tap(() => store.update(deleteEntities(data['_id']))));
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getFruitNinjaGameList();
    }
  }
}

interface IfruitNinjaGameListResponse {
  list: IFruitNinjaGame[];
  totalCount: number;
}
